/**
 * useTaskModalRealtime - Hook de Realtime para o modal de detalhes da tarefa
 * 
 * Subscreve canal Realtime nas tabelas `task_comments` e `tasks` filtrado por task_id/id
 * e invalida caches relevantes ao receber eventos INSERT/UPDATE/DELETE.
 * 
 * Ativa apenas quando taskId é fornecido (modal aberto).
 * Cleanup automático ao fechar modal (taskId = null).
 * 
 * @version 2.1.0
 * RELEASE: 2026-04-11 v1.0.0 - Criação inicial (task_comments)
 * RELEASE: 2026-04-11 v2.0.0 - Adiciona subscrição na tabela tasks para sincronizar
 *   status, fase, responsável e pausas em tempo real entre abas/listagem e modal aberto
 * RELEASE: 2026-08-21 v2.1.0 - Corrige erro "cannot add `postgres_changes` callbacks
 *   ... after `subscribe()`". O supabase-js reaproveita o canal quando o tópico já
 *   existe; com duas instâncias do modal da MESMA tarefa (ex.: subabas Normal/
 *   Retenção/Jurídico remontando antes do cleanup), a 2ª instância tentava
 *   registrar listeners num canal já inscrito e derrubava a tela. Agora o tópico
 *   recebe um sufixo único por instância do hook.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger dedicado para este hook
const logger = createLogger('TaskModalRealtime');

/**
 * Chaves de cache invalidadas ao receber eventos de task_comments
 */
const COMMENT_QUERY_KEYS = [
  'task-comments',
  'task-details',
  'task-pauses',
];

/**
 * Chaves de cache invalidadas ao receber eventos de tasks (status, fase, etc.)
 */
const TASK_QUERY_KEYS = [
  'task-details',
  'task-pauses',
  'task-activity-log',
  'task-kanban-column',
  'task-kanban-counts',
  'tasks-paginated',
];

/**
 * Hook que subscreve Realtime nas tabelas task_comments e tasks
 * para manter o modal de tarefa sincronizado em tempo real.
 * 
 * @param taskId - ID da tarefa aberta no modal (null = modal fechado, não subscreve)
 */
export function useTaskModalRealtime(taskId: string | null) {
  const queryClient = useQueryClient();

  // v2.1.0: identificador único por instância do hook. Evita colisão de tópico
  // quando o mesmo taskId é aberto por mais de uma instância do modal.
  const instanceIdRef = useRef<string>(
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2)
  );

  useEffect(() => {
    // Não cria canal se modal está fechado
    if (!taskId) return;

    logger.debug('Iniciando Realtime para modal de tarefa', { taskId });

    // Canal único com dois listeners: task_comments e tasks
    const channel = supabase
      .channel(`task-modal-rt-${taskId}-${instanceIdRef.current}`)
      // Listener 1: Alterações em task_comments (andamentos, comentários)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'task_comments',
          filter: `task_id=eq.${taskId}`,
        },
        (payload) => {
          logger.debug('Realtime: task_comments alterado', {
            eventType: payload.eventType,
            taskId,
          });

          // Invalida caches relacionados a comentários/andamentos
          for (const key of COMMENT_QUERY_KEYS) {
            queryClient.invalidateQueries({ queryKey: [key, taskId] });
          }
        }
      )
      // Listener 2: Alterações na própria tarefa (status, fase, responsável, pausas)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tasks',
          filter: `id=eq.${taskId}`,
        },
        (payload) => {
          logger.debug('Realtime: tasks alterado', {
            eventType: payload.eventType,
            taskId,
          });

          // Invalida caches relacionados à tarefa e views que a exibem
          for (const key of TASK_QUERY_KEYS) {
            // Chaves com taskId específico
            if (key === 'task-details' || key === 'task-pauses' || key === 'task-activity-log') {
              queryClient.invalidateQueries({ queryKey: [key, taskId] });
            } else {
              // Chaves globais (kanban, listagem) — invalida todas
              queryClient.invalidateQueries({ queryKey: [key] });
            }
          }
        }
      )
      // Listener 3: Alterações em checklist da tarefa (grupos e itens)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'task_checklist_groups' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['task-checklist', taskId] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'task_checklist_items' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['task-checklist', taskId] });
        }
      )
      // Listener 4: Comentários e menções em itens de checklist (Fase 2)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'task_checklist_item_comments' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['task-checklist', taskId] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'task_checklist_item_comment_mentions' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['task-checklist', taskId] });
        }
      )

      .subscribe((status) => {
        logger.debug('Realtime task modal subscription status:', status);
      });

    // Cleanup ao fechar modal ou mudar de tarefa
    return () => {
      logger.debug('Removendo Realtime do modal de tarefa', { taskId });
      supabase.removeChannel(channel);
    };
  }, [taskId, queryClient]);
}
